import { Suspense, useEffect, useState, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Button } from "src/elements";
import { useTranslation } from "src/i18n";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useUser } from "src/atoms/user";
import { getLoginToken, pages, removeLoginToken } from "src/utils";
import { useNavigation } from "src/utils/navigation";
import { Page } from "../Page/Page";
import { PageStatus } from "../PageStatus/PageStatus";
import { Modals } from "../Modals/Modals";
import { Tour } from "../Tour/Tour";
import { TourOffer } from "../Tour/TourOffer";
import { useApi } from "src/api";
import { getCurrentRelativeUrl, getRedirectUrl, setRedirectUrl } from "src/utils/sessionStorage";
import {
  PLANOGRAM_VIEWER_HOME,
  isPathOpenToPlanogramViewer,
  isPlanogramViewerOnlyUser,
} from "src/utils/planogramViewerAccess";

const subscribeLoginToken = (changed: () => void) => {
  const listener = (event: StorageEvent) => {
    if (event.key === "token" || event.key === null) changed();
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
};

export const IsAuthorized = () => {
  const { pathname } = useLocation();
  const { navigate } = useNavigation();
  const { getVerifyUserApi } = useApi();
  const { user, setUser } = useUser();

  const { t } = useTranslation("common");
  const token = useSyncExternalStore(subscribeLoginToken, getLoginToken, () => null);
  // Opaque generation keeps credentials out of query keys and isolates replacement sessions.
  const [identity, setIdentity] = useState(() => ({ token, generation: crypto.randomUUID() }));
  const [verifiedToken, setVerifiedToken] = useState(user ? token : null);
  if (identity.token !== token) setIdentity({ token, generation: crypto.randomUUID() });
  const generation = identity.generation;
  const authorized = !!user && !!token && verifiedToken === token && identity.token === token;
  const verification = useQuery({
    queryKey: ["auth", "verify-user", generation],
    queryFn: async () => ({ user: await getVerifyUserApi(), generation }),
    enabled: !authorized && !!token,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });
  const rejected =
    isAxiosError(verification.error) &&
    (verification.error.response?.status === 401 || verification.error.response?.status === 403);
  useEffect(() => {
    if (authorized || getLoginToken() !== token || identity.token !== token) return;
    if (user) setUser(null as unknown as typeof user);
    if (getRedirectUrl() === null) setRedirectUrl(getCurrentRelativeUrl());
    if (!token || rejected) {
      if (rejected) removeLoginToken();
      navigate(pages.login);
    } else if (verification.data?.generation === generation && verification.data.user) {
      setVerifiedToken(token);
      setUser(verification.data.user);
    }
  }, [
    authorized,
    user,
    token,
    identity.token,
    rejected,
    verification.data,
    generation,
    navigate,
    setUser,
  ]);

  // Never expose protected data until the server has verified the session.
  if (!authorized) {
    return (
      <Page hasSidebar={false}>
        <PageStatus
          label={t(
            verification.isError || (verification.isSuccess && !verification.data?.user)
              ? "authVerificationUnavailable"
              : "authVerifying",
          )}
          icon="spinner"
        />
        {(verification.isError || (verification.isSuccess && !verification.data?.user)) &&
          !rejected &&
          token && (
            <Button
              color="primary"
              onClick={() => void verification.refetch()}
              isDisabled={verification.isFetching}
            >
              {t("authRetryVerification")}
            </Button>
          )}
      </Page>
    );
  }

  // A delivered-planogram viewer only ever sees the read-only planogram pages.
  const isPlanogramViewerOnly = isPlanogramViewerOnlyUser(user);
  if (isPlanogramViewerOnly && !isPathOpenToPlanogramViewer(pathname)) {
    return <Navigate to={PLANOGRAM_VIEWER_HOME} replace />;
  }

  // Page content is only rendered once user is authorized.
  return (
    <>
      <Suspense
        fallback={
          <Page>
            <PageStatus label="Loading..." icon="spinner" />
          </Page>
        }
      >
        <Outlet />
      </Suspense>
      <Modals />
      {/* The product tour walks the editing pages, which a planogram viewer cannot open. */}
      {!isPlanogramViewerOnly && <TourOffer />}
      {!isPlanogramViewerOnly && <Tour />}
    </>
  );
};
